"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Mail, User, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { UnderlineInput } from "@/components/UnderlineInput";

export default function PublicJoinPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/sessions/join/public", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to join");

      // Store guest token for WebSocket auth
      sessionStorage.setItem("iris_guest_token", data.guestToken);
      sessionStorage.setItem("iris_participant_id", data.participantId);
      sessionStorage.setItem("iris_participant_name", name);

      router.push(`/session/${data.sessionId}/live`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex w-full max-w-md flex-col items-center gap-8 px-4">
      <Logo variant="icon" size={160} className="drop-shadow-[0_0_30px_rgba(221,30,37,0.25)]" />

      <div className="text-center">
        <h1 className="font-heading text-2xl font-bold text-white">Join Live Session</h1>
        <p className="mt-2 text-sm text-[var(--iris-text-muted)]">
          Enter your details and the 4-digit session code
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex w-full flex-col gap-5">
        <div>
          <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-[var(--iris-text-muted)]">
            Your name
          </label>
          <UnderlineInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
            icon={<User className="h-4 w-4" />}
            required
          />
        </div>

        <div>
          <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-[var(--iris-text-muted)]">
            Email address
          </label>
          <UnderlineInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            icon={<Mail className="h-4 w-4" />}
            required
          />
        </div>

        <div>
          <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-[var(--iris-text-muted)]">
            Session code
          </label>
          <UnderlineInput
            value={code}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 4);
              setCode(val);
            }}
            placeholder="1234"
            icon={<Hash className="h-4 w-4" />}
            className="font-mono text-center text-lg tracking-[0.3em]"
            maxLength={4}
            inputMode="numeric"
            required
          />
        </div>

        {error && <p className="text-center text-sm text-[var(--iris-red)]">{error}</p>}

        <Button
          type="submit"
          disabled={loading || code.length < 4 || !name || !email}
          className="mt-2 h-12 text-base font-semibold tracking-wide"
        >
          {loading ? "Joining..." : "JOIN SESSION"}
        </Button>

        <div className="text-center text-sm text-[var(--iris-text-muted)]">
          Already have an account?{" "}
          <Link href="/login" className="text-[var(--iris-red)] hover:underline">
            Log in
          </Link>
        </div>
      </form>
    </div>
  );
}
